import serializeNode from "dom-serializer";
import HtmlToReact, { Parser } from "html-to-react";
import { produce } from "immer";
import React, { useContext, useEffect } from "react";
import { DndProvider } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";
import shortid from "shortid";

import { ContentStyles } from "@examspark/common-ui";
import Math from "@examspark/common-ui/dist/components/Math";
import { scoreGap } from "@examspark/score";
import { StemTextGapMatch, Stem } from "@examspark/types";

import {
  addTokenOnDrop,
  hideTokenAfterUse,
  replaceTokenOnDrop,
} from "../common/drag/dndUtils";
import { Action, QuestionState } from "../context/reducer";
import QuestionContext from "../QuestionContext";

import { DroppableType } from "./common/image-map/ImageMap";
import Prompt from "./shared/Prompt";
import TextGap, { GapDrop } from "./shared/TextGap";
import Token, { DndItem } from "./shared/Token";

// this item type's data structure was missing the "drops" object
// parent that we use to discriminate between the different response types,
// so we have to define it here
type OldTextGapMatchResponse = {
  isNewReponseShape: never;
  [gapId: string]: DndItem;
};
type NewTextGapMatchResponse = {
  isNewReponseShape: true;
  drops: Array<Stem.Drop>;
};
export type TextGapMatchResponse =
  | OldTextGapMatchResponse
  | NewTextGapMatchResponse;

const Tokens = ({ tokens }: { tokens: DroppableType[] }) => {
  return (
    <div className="item-player-tokens">
      {tokens?.map((opt) => {
        if (opt.type !== "text") return null;

        return (
          <Token key={opt.item.id} data={opt.item} useDragType="token" inline />
        );
      })}
    </div>
  );
};

export type TextGapMatchProps = {
  data: StemTextGapMatch;
};
const TextGapMatch = (props: TextGapMatchProps) => {
  const [player, dispatchPlayer] = useContext(QuestionContext);

  useRevealKey(props, player, dispatchPlayer);

  // map between old and new response shapes
  const response = handleOldNewResponseShape(props, player);

  const tokens = hideTokenAfterUse(
    props.data.content.options.map((o) => ({
      type: "text",
      item: o,
    })),
    response?.drops ?? [],
  );

  const reactElement = processBodyHtml(props, response, player, dispatchPlayer);

  return (
    <div>
      <Prompt data={props.data.content.prompt} />
      {/* @ts-expect-error children */}
      <DndProvider
        backend={TouchBackend}
        options={{
          enableMouseEvents: true,
          enableHoverOutsideTarget: true,
        }}
        context={window}
      >
        <div className="item-player-inline-text" key={props.data.id}>
          <ContentStyles>{reactElement}</ContentStyles>
        </div>
        <Tokens tokens={tokens} />
      </DndProvider>
    </div>
  );
};
export default TextGapMatch;

const useRevealKey = (
  props: TextGapMatchProps,
  player: any,
  dispatchPlayer: React.Dispatch<Action>,
) => {
  useEffect(() => {
    if (player.revealKey) {
      const keys: NewTextGapMatchResponse = {
        isNewReponseShape: true,
        drops: [],
      };

      // add all correct answers to the response
      props.data.scoring?.pairs?.forEach((p) => {
        if (p.dst && p.src && p.isKey) {
          keys.drops.push({
            draggableId: p.src,
            droppableId: p.dst,
            instanceId: shortid.generate(),
          });
        }
      });

      const tokens = props.data.content.options.map((o) => ({
        type: "text" as const,
        item: o,
      }));

      // ensure we only have one response in gaps that don't allow multiple tokens
      // and that we respect any token's maxUsage
      keys.drops = keys.drops.reduce((acc, cur) => {
        const allowMultipleTokens =
          props.data.content.gaps?.[cur.droppableId]?.allowMultipleTokens;
        const gapIsFilled = acc.some((a) => a.droppableId === cur.droppableId);

        if (allowMultipleTokens || !gapIsFilled) {
          // ensure the token is still available to use
          const token = hideTokenAfterUse(tokens, acc).find(
            (t) => t.item.id === cur.draggableId,
          );
          if (token) {
            acc.push(cur);
          }
        }
        return acc;
      }, [] as Array<Stem.Drop>);

      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          score: true,
          response: keys,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.revealKey]);
};

const handleOldNewResponseShape = (
  props: TextGapMatchProps,
  player: any,
): NewTextGapMatchResponse => {
  const response = player?.response?.[props.data.id]?.isNewReponseShape
    ? (player?.response?.[props.data.id] as NewTextGapMatchResponse)
    : ({
        isNewReponseShape: true,
        drops: Object.entries(
          (player?.response?.[props.data.id] ?? {}) as OldTextGapMatchResponse,
        ).map(
          ([k, v]) =>
            ({
              draggableId: v.id,
              droppableId: k,
              instanceId: shortid.generate(),
            }) as Stem.Drop,
        ),
      } as NewTextGapMatchResponse);
  return response;
};

const processBodyHtml = (
  props: TextGapMatchProps,
  response: NewTextGapMatchResponse,
  player: QuestionState,
  dispatchPlayer: React.Dispatch<Action>,
) => {
  const handleDrop = (gapId: string, item: DndItem) => {
    let rsp: NewTextGapMatchResponse = response ?? {
      isNewReponseShape: true,
      drops: [],
    };

    rsp = produce(rsp, (draft) => {
      if (props.data.content.gaps?.[gapId]?.allowMultipleTokens) {
        draft.drops = addTokenOnDrop(gapId, item, draft.drops);
      } else {
        draft.drops = replaceTokenOnDrop(gapId, item, draft.drops);
      }
    });

    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: rsp,
      },
    });
  };

  // todo: prep this for handling multiple tokens
  const handleTokenRemove = (instanceId: string) => {
    let rsp = response;

    rsp = produce(rsp, (draft) => {
      draft.drops = draft.drops.filter((d) => d.instanceId !== instanceId);
    });

    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: rsp,
      },
    });
  };

  const droppedItems = (gapId: string) => {
    const options = props.data.content.options;
    const drops = response.drops.filter((d) => d.droppableId === gapId);
    const items =
      drops
        .map((drop) => ({
          drop,
          option: options.find(({ id }) => id === drop.draggableId),
        }))
        .filter((dropped): dropped is GapDrop => !!dropped.option) ?? [];
    return items;
  };

  let reactElement;
  if (props.data?.content?.text?.body) {
    const htmlToReactParser = new Parser();
    const processNodeDefinitions = HtmlToReact.ProcessNodeDefinitions();
    reactElement = htmlToReactParser.parseWithInstructions(
      props.data.content.text.body,
      () => true,
      [
        {
          // This is REQUIRED, it tells the parser
          // that we want to insert our React
          // component as a child
          shouldProcessNode: function (node: any) {
            return node.attribs && node.attribs["data-gap-id"];
          },
          processNode: function (node: any) {
            const gapId = node.attribs["data-gap-id"] as string;

            const drops = droppedItems(gapId);
            const pairs =
              props.data.scoring?.pairs?.filter((p) => p.dst === gapId) ?? [];

            // Mark Responses
            let mark: Stem.Mark = undefined;
            if (pairs.length && player.scoreResponses) {
              mark = scoreGap(
                props.data.scoring?.gapMatchScoringMethod,
                drops,
                pairs,
                props.data.content.gaps?.[gapId]?.allowMultipleTokens,
              )
                ? "correct"
                : "incorrect";
            }

            const gapProps: React.ComponentProps<typeof TextGap> & {
              key: React.Key;
            } = {
              key: gapId,
              mark: mark,
              gapId: gapId,
              onDrop: handleDrop,
              dropped: drops,
              onTokenRemove: handleTokenRemove,
            };

            return React.createElement(TextGap, gapProps);
          },
        },
        {
          // pre-process MathML
          shouldProcessNode: (node: any) => node?.name === "math",
          processNode: function (node: any) {
            return <Math display={false} mathml={serializeNode(node)} />;
          },
        },
        {
          // Anything else
          shouldProcessNode: function (node: any) {
            return true;
          },
          processNode: processNodeDefinitions.processDefaultNode,
        },
      ],
    );
  } else {
    reactElement = <div></div>;
  }

  return reactElement;
};
